import { INVOICEABLE_STATUSES, INVOICED_STATUS, NON_BILLABLE_STATUSES } from '../types/enums.js';
import { isOutOfScopeCategory } from './milestone.js';

/**
 * Work in progress ("Ufaktureret WIP") and the order book: which tasks in OnePro are still work
 * going on, and which are settled.
 */

/**
 * The OnePro statuses where a task's work is settled - nothing more is in progress on it:
 *
 *   ready         `Ready for invoicing`, `Partially Ready for Invoicing` - finished, for the invoice.
 *   invoiced      `Invoiced` - on an invoice already.
 *   not sold      `Not Billable`, `Internal`, `Sales` - work no customer pays for.
 *
 * Compared trimmed and in any case: OnePro's spelling has varied ('Ready for invoicing').
 */
export const SETTLED_STATUSES: readonly string[] = [
  ...INVOICEABLE_STATUSES,
  INVOICED_STATUS,
  ...NON_BILLABLE_STATUSES,
];

const SETTLED = new Set(SETTLED_STATUSES.map((status) => status.toLowerCase()));

/** Is the task's work settled - see `SETTLED_STATUSES`? */
export function isSettledStatus(status: string | null | undefined): boolean {
  return SETTLED.has(status?.trim().toLowerCase() ?? '');
}

/** A task as the WIP rule needs it. */
export interface WipTask {
  /** OnePro's `todos.status`. */
  status: string | null;
  /** The phase it is in on the board - `todo_categories.name`. Null when it is in none. */
  category_name?: string | null;
  /** The phase is closed in OnePro - see `isMilestoneClosed`. */
  category_closed?: boolean;
}

/**
 * Does the task count as unbilled work in progress? Decided 15-10-2026:
 *
 *   status     not settled - see `SETTLED_STATUSES`. What is ready is for the invoice, not in
 *              progress; what is invoiced or not sold never will be.
 *   General    not in the board's `General` phase - work outside the agreement.
 *   open       not in a closed phase. Its estimate is given up when it closes (`unusedEstimate`),
 *              so the work in it is not in progress any more.
 *
 * A task in no phase has nothing to close and counts. The time on it is valued at the agreement's
 * rate by the caller, and a task already on a Codepass invoice is left out there too.
 */
export function countsAsWipTask(task: WipTask): boolean {
  if (isSettledStatus(task.status)) return false;
  if (isOutOfScopeCategory(task.category_name)) return false;
  return !task.category_closed;
}
